import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, KeyRound } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { ApiError, NetworkError } from '../../lib/api';
import { copyText } from '../../lib/clipboard';
import { useAuth } from '../auth/AuthProvider';
import { SiteTree } from '../users/CameraAccess';
import type { AccessMode } from '../users/api';
import {
  cameraPath,
  createIntegration,
  deleteIntegration,
  getIntegrationCredentials,
  integrationCamerasKey,
  integrationMessages,
  integrationsKey,
  listIntegrationCameras,
  resetIntegrationPassword,
  revokeIntegration,
  rtspEndpoint,
  updateIntegration,
  type Integration,
  type IntegrationBody,
} from './api';

interface Props {
  integration?: Integration;
  /** rtsp://host:port, for the instructions of a new integration. */
  rtspURL: string;
  onClose: () => void;
  /** Shows another (or the refreshed) integration in the drawer. */
  onOpen: (i: Integration) => void;
}

interface Problem {
  fields: Record<string, string>;
  form: string | null;
}

function toProblem(err: unknown): Problem {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      const fields: Record<string, string> = {};
      for (const [f, code] of Object.entries(err.fields)) {
        fields[f] = integrationMessages[code] ?? 'Valor inválido.';
      }
      return { fields, form: fields.device_ids ?? fields.site_ids ?? null };
    }
    if (err.status === 403) return { fields: {}, form: 'Você não tem permissão para esta ação.' };
    if (err.status === 404) return { fields: {}, form: 'Esta integração não existe mais.' };
  }
  if (err instanceof NetworkError)
    return { fields: {}, form: 'Não foi possível contatar o servidor. Tente novamente.' };
  return { fields: {}, form: 'Ocorreu um erro inesperado. Tente novamente.' };
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

/**
 * Creates or edits an integration (ADR-024): a name and the cameras it may
 * receive. Editing shows the credentials and how to set them in the other
 * system; the password is revealed on request (audited).
 */
export function IntegrationDrawer({ integration, rtspURL, onClose, onOpen }: Props) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(integration?.name ?? '');
  const [mode, setMode] = useState<AccessMode>(integration?.mode ?? 'assigned');
  const [clientIds, setClientIds] = useState<string[]>(integration?.client_ids ?? []);
  const [siteIds, setSiteIds] = useState<string[]>(integration?.site_ids ?? []);
  const [deviceIds, setDeviceIds] = useState<string[]>(integration?.device_ids ?? []);
  const [problem, setProblem] = useState<Problem>({ fields: {}, form: null });
  const [confirm, setConfirm] = useState<'revoke' | 'delete' | 'password' | null>(null);

  const body = (): IntegrationBody => {
    const b: IntegrationBody = {};
    if (!integration || name.trim() !== integration.name) b.name = name;
    if (
      !integration ||
      mode !== integration.mode ||
      !sameSet(siteIds, integration.site_ids) ||
      !sameSet(clientIds, integration.client_ids ?? []) ||
      !sameSet(deviceIds, integration.device_ids)
    ) {
      b.mode = mode;
      b.site_ids = mode === 'all' ? [] : siteIds;
      b.client_ids = mode === 'all' ? [] : clientIds;
      b.device_ids = mode === 'all' ? [] : deviceIds;
    }
    return b;
  };
  const dirty = !integration || Object.keys(body()).length > 0;

  /** After saving: a new integration opens with its credentials; other
   * actions stay on it with the refreshed state. */
  const done = async (i: Integration, message: string, stay: boolean) => {
    await qc.invalidateQueries({ queryKey: integrationsKey });
    notify(message);
    if (stay) onOpen(i);
    else onClose();
  };
  const fail = (err: unknown) => {
    setConfirm(null);
    setProblem(toProblem(err));
  };

  const save = useMutation({
    mutationFn: () =>
      integration
        ? updateIntegration(request, integration.id, body())
        : createIntegration(request, body()),
    onSuccess: (i) =>
      done(
        i,
        integration ? 'Integração atualizada.' : `Integração ${i.name} criada.`,
        !integration,
      ),
    onError: fail,
  });
  const password = useMutation({
    mutationFn: () => resetIntegrationPassword(request, integration?.id ?? ''),
    onSuccess: (i) => done(i, 'Nova senha gerada. A anterior deixou de funcionar.', true),
    onError: fail,
  });
  const revoke = useMutation({
    mutationFn: () => revokeIntegration(request, integration?.id ?? ''),
    onSuccess: (i) => done(i, `Acesso de ${i.name} revogado.`, true),
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => deleteIntegration(request, integration?.id ?? ''),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: integrationsKey });
      notify('Integração excluída.');
      onClose();
    },
    onError: fail,
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem({ fields: {}, form: null });
    save.mutate();
  };
  const toggle = (setter: typeof setSiteIds) => (id: string, on: boolean) => {
    setter((list) => (on ? [...new Set([...list, id])] : list.filter((x) => x !== id)));
  };

  const busy = save.isPending || password.isPending || revoke.isPending || remove.isPending;
  return (
    <Drawer
      title={integration ? integration.name : 'Nova integração'}
      {...(integration
        ? {
            description: `Usuário ${integration.username} · ${integration.status === 'active' ? 'ativa' : 'revogada'}`,
          }
        : { description: 'Um sistema que recebe vídeo do Pro Guard' })}
      onClose={onClose}
      footer={
        <>
          {integration && (
            <Button
              className="mr-auto text-crit!"
              onClick={() => {
                setConfirm('delete');
              }}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" form="integration-form" disabled={busy || !dirty}>
            {save.isPending ? 'Salvando…' : integration ? 'Salvar alterações' : 'Criar integração'}
          </Button>
        </>
      }
    >
      {integration && (
        <Credentials key={integration.id} integration={integration} rtspURL={rtspURL} />
      )}
      <form
        id="integration-form"
        onSubmit={submit}
        noValidate
        className={`space-y-4 ${integration ? 'mt-6 border-t border-line pt-5' : ''}`}
      >
        <FormProblem message={problem.form} />
        <Field
          label="Nome"
          hint="O sistema que vai receber o vídeo, ex.: Somaseg."
          error={problem.fields.name}
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>

        <section aria-labelledby="integration-cameras" className="space-y-2">
          <h3 id="integration-cameras" className="text-[13px] font-medium text-fg">
            Câmeras que o sistema recebe
          </h3>
          <div role="radiogroup" aria-label="Câmeras que o sistema recebe" className="space-y-1.5">
            {(
              [
                ['assigned', 'Somente os locais e câmeras selecionados'],
                ['all', 'Todas as câmeras da organização'],
              ] as [AccessMode, string][]
            ).map(([m, label]) => (
              <label key={m} className="flex cursor-pointer items-center gap-2 text-[13px] text-fg">
                <input
                  type="radio"
                  name="integration-mode"
                  checked={mode === m}
                  onChange={() => {
                    setMode(m);
                  }}
                  className="size-3.5 accent-[var(--color-accent-strong)]"
                />
                {label}
              </label>
            ))}
          </div>
          {mode === 'assigned' && (
            <>
              <p className="text-xs text-fg-muted">
                Marcar o local inclui as câmeras cadastradas nele depois; marcar um gravador inclui
                os seus canais. O número ao lado de cada câmera é o canal que o outro sistema usa.
              </p>
              <SiteTree
                siteIds={siteIds}
                deviceIds={deviceIds}
                clientIds={clientIds}
                onClient={toggle(setClientIds)}
                onSite={toggle(setSiteIds)}
                onDevice={toggle(setDeviceIds)}
              />
              {clientIds.length + siteIds.length + deviceIds.length === 0 && (
                <p className="text-xs text-warn">
                  Nada selecionado: o sistema não receberá nenhuma câmera.
                </p>
              )}
            </>
          )}
        </section>
      </form>

      {integration && (
        <section
          aria-labelledby="integration-access"
          className="mt-6 space-y-3 border-t border-line pt-5"
        >
          <h3 id="integration-access" className="text-[13px] font-medium text-fg">
            Acesso
          </h3>
          <p className="text-xs text-fg-muted">
            Gerar uma nova senha invalida a atual na hora; atualize o outro sistema em seguida.
            {integration.status === 'revoked' &&
              ' Esta integração está revogada: gerar uma nova senha a reativa.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy}
              onClick={() => {
                setConfirm('password');
              }}
            >
              <KeyRound aria-hidden="true" className="size-4" strokeWidth={1.75} />
              Gerar nova senha
            </Button>
            {integration.status === 'active' && (
              <Button
                className="text-crit!"
                disabled={busy}
                onClick={() => {
                  setConfirm('revoke');
                }}
              >
                Revogar acesso
              </Button>
            )}
          </div>
        </section>
      )}

      {confirm && integration && (
        <ConfirmDialog
          title={
            confirm === 'delete'
              ? `Excluir ${integration.name}?`
              : confirm === 'revoke'
                ? `Revogar o acesso de ${integration.name}?`
                : `Gerar nova senha para ${integration.name}?`
          }
          confirmLabel={
            confirm === 'delete'
              ? 'Excluir integração'
              : confirm === 'revoke'
                ? 'Revogar acesso'
                : 'Gerar nova senha'
          }
          busy={busy}
          onCancel={() => {
            setConfirm(null);
          }}
          onConfirm={() => {
            if (confirm === 'delete') remove.mutate();
            else if (confirm === 'revoke') revoke.mutate();
            else password.mutate();
          }}
        >
          {confirm === 'password'
            ? 'A senha atual deixa de funcionar na hora; o outro sistema precisa receber a nova.'
            : 'O sistema deixa de receber vídeo na próxima conexão. Esta ação fica registrada na auditoria.'}
        </ConfirmDialog>
      )}
    </Drawer>
  );
}

/**
 * The credentials and how to set them in another system. The password is
 * masked until requested (each view is audited on the server).
 */
function Credentials({ integration: i, rtspURL }: { integration: Integration; rtspURL: string }) {
  const { request } = useAuth();
  const { host, port } = rtspEndpoint(i.rtsp_url || rtspURL);
  const [password, setPassword] = useState(i.password ?? '');
  const [visible, setVisible] = useState(!!i.password);
  const reveal = useMutation({
    mutationFn: () => getIntegrationCredentials(request, i.id),
    onSuccess: (c) => {
      setPassword(c.password ?? '');
      setVisible(true);
    },
  });
  /** The password, fetched once when needed. */
  const ensure = async () => password || (await reveal.mutateAsync()).password || '';
  const cameras = useQuery({
    queryKey: integrationCamerasKey(i.id),
    queryFn: () => listIntegrationCameras(request, i.id),
  });
  const [picked, setPicked] = useState<number | null>(null);
  const [sub, setSub] = useState(false);
  const list = cameras.data ?? [];
  const chosen =
    list.find((c) => c.number === picked) ?? list.find((c) => c.enabled) ?? list[0] ?? null;
  const number = chosen?.number ?? 0;
  /** The complete address of a camera, with the credentials. */
  const fullURL = (pw: string) =>
    `rtsp://${i.username}:${pw}@${host}:${port}${cameraPath(number, sub)}`;
  return (
    <section aria-labelledby="integration-credentials" className="space-y-4">
      <h3 id="integration-credentials" className="text-[13px] font-medium text-fg">
        Credenciais de acesso ao vídeo
      </h3>
      <dl className="grid grid-cols-[7rem_1fr] items-center gap-x-3 gap-y-2 text-[13px]">
        <dt className="text-fg-subtle">Servidor</dt>
        <dd>
          <Copyable value={host} label="Copiar servidor" />
        </dd>
        <dt className="text-fg-subtle">Porta RTSP</dt>
        <dd>
          <Copyable value={port} label="Copiar porta" />
        </dd>
        <dt className="text-fg-subtle">Usuário</dt>
        <dd>
          <Copyable value={i.username} label="Copiar usuário" />
        </dd>
        <dt className="text-fg-subtle">Senha</dt>
        <dd className="flex items-center gap-1">
          <Copyable
            value={visible ? password : '••••••••••••••••'}
            copy={ensure}
            label="Copiar senha"
          />
          <button
            type="button"
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={visible}
            disabled={reveal.isPending}
            onClick={() => {
              if (visible) setVisible(false);
              else if (password) setVisible(true);
              else reveal.mutate();
            }}
            className="rounded p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg"
          >
            {visible ? (
              <EyeOff aria-hidden="true" className="size-3.5" />
            ) : (
              <Eye aria-hidden="true" className="size-3.5" />
            )}
          </button>
        </dd>
      </dl>
      {reveal.isError && (
        <p role="alert" className="text-xs text-crit">
          Não foi possível mostrar a senha. Tente novamente.
        </p>
      )}
      <div className="space-y-2 text-xs text-fg-muted">
        <h4 className="text-[13px] font-medium text-fg">Como cadastrar no outro sistema</h4>
        <p>
          O Pro Guard responde como um gravador <strong className="text-fg">Hikvision</strong>. No
          Somaseg, use o modelo Hikvision (em NVR/DVR ou Câmera IP) com o servidor, a porta, o
          usuário e a senha acima; o <strong className="text-fg">canal</strong> é o número da
          câmera, que aparece ao lado de cada câmera e na ficha do dispositivo.
        </p>
        <p>Endereço completo, se o sistema pedir (modelo personalizado):</p>
        <div className="space-y-2 rounded-md border border-line bg-canvas p-3 text-fg">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Câmera" hint="Somente as câmeras liberadas para esta integração.">
              {({ id, describedBy }) => (
                <select
                  id={id}
                  value={chosen?.number ?? ''}
                  disabled={list.length === 0}
                  onChange={(e) => {
                    setPicked(Number(e.target.value));
                  }}
                  aria-describedby={describedBy}
                  className={`${inputClass} w-72`}
                >
                  {list.map((c) => (
                    <option key={c.number} value={c.number}>
                      {`${String(c.number)} — ${c.name}${c.enabled ? '' : ' (desativada)'}`}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Vídeo">
              {({ id }) => (
                <select
                  id={id}
                  value={sub ? 'sub' : 'main'}
                  onChange={(e) => {
                    setSub(e.target.value === 'sub');
                  }}
                  className={`${inputClass} w-40`}
                >
                  <option value="main">Principal (01)</option>
                  <option value="sub">Secundário (02)</option>
                </select>
              )}
            </Field>
          </div>
          {cameras.isPending ? (
            <p role="status" className="text-xs text-fg-muted">
              Carregando câmeras…
            </p>
          ) : cameras.isError ? (
            <p role="alert" className="text-xs text-crit">
              Não foi possível carregar as câmeras liberadas.
            </p>
          ) : list.length === 0 ? (
            <p className="text-xs text-warn">
              Nenhuma câmera liberada: escolha locais ou câmeras abaixo e salve.
            </p>
          ) : (
            <>
              {chosen && !chosen.enabled && (
                <p className="text-xs text-warn">
                  Esta câmera está desativada no Pro Guard: o vídeo não será entregue.
                </p>
              )}
              <Copyable
                value={fullURL(visible ? password : '••••••••')}
                copy={async () => fullURL(await ensure())}
                label="Copiar endereço RTSP completo"
                wrap
              />
            </>
          )}
        </div>
        <p>
          Também serve para testar no VLC. Final <span className="font-mono">01</span> é o vídeo
          principal e <span className="font-mono">02</span> o secundário. Com usuário e senha em
          campos separados, use{' '}
          <span className="font-mono break-all">
            {`rtsp://%user%:%senha%@${host}:${port}/Streaming/Channels/%channel%01`}
          </span>
          .
        </p>
      </div>
    </section>
  );
}

function Copyable({
  value,
  label,
  copy,
  wrap = false,
}: {
  value: string;
  label: string;
  /** What to copy when it is not the shown value (a masked password). */
  copy?: () => Promise<string>;
  /** Long values wrap instead of being cut. */
  wrap?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex items-center gap-2">
      <span className={`min-w-0 font-mono text-xs text-fg ${wrap ? 'break-all' : 'truncate'}`}>
        {value || '—'}
      </span>
      <button
        type="button"
        aria-label={label}
        onClick={() => {
          void (copy ? copy() : Promise.resolve(value)).then(copyText).then((ok) => {
            if (!ok) return;
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 1500);
          });
        }}
        className="rounded p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg"
      >
        {copied ? (
          <Check aria-hidden="true" className="size-3.5" />
        ) : (
          <Copy aria-hidden="true" className="size-3.5" />
        )}
      </button>
    </span>
  );
}
